import React, { forwardRef, useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';

import { accountsActions, useStore } from '@modules';
import { RecentAccountTransaction } from '@modules/accounts/types';
import { PhoneFormatsType } from '@modules/global/types';

import { useCustomContext } from '../../context';
import SearchInput from '../SearchInput';

import style from './RecentTransfers.scss';

const cn = bemCssModules(style, 'RecentTransfers');
const ti = getTestId('RecentTransfers');

export type RecentTransfersType = {
  countries?: { value: PhoneFormatsType; label: string }[];
  error?: string;
};

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

const CustomOption = forwardRef<HTMLButtonElement, { date: string; data: DataElementType }>(
  (props, ref) => {
    const { date, data, ...rest } = props;
    const { iban, country, full_name } = data;
    return (
      // TODO: required rest type for CustomOption
      <button className={cn('option')} ref={ref} type="button" {...rest}>
        <div className={cn('number', ['h4'])}>{iban}</div>
        <small className={cn('date', ['h5'])}>{date}</small>
        <small className={cn('info')}>
          {country} &#183; {full_name}
        </small>
      </button>
    );
  }
);

type DataElementType = RecentAccountTransaction & { value: string; label: string };

export const RecentTransfers: CustomFC<RecentTransfersType> = ({ className, countries, error }) => {
  const { t } = useTranslation();
  const [{ user }, dispatch] = useStore();
  const languageCode = user?.data?.language_code || window.navigator.language;
  const [data, setData] = useState<DataElementType[]>([]);
  const { context, setContext } = useCustomContext();
  const { iban } = context;

  useEffect(() => {
    dispatch(accountsActions.getAccountRecentTransactions())
      .unwrap()
      .then(res => {
        const formated = res.data.map(obj => ({ ...obj, value: obj.created_at, label: obj.iban }));
        setData(formated);
      })
      .catch(err => err);
  }, []);

  const handleChange = (el: DataElementType) => {
    const currentCountry = countries?.find(country => country.value === el.country)?.label;
    setContext(draft => {
      draft.country = currentCountry || '';
      draft.iban = el.iban;
      draft.recipient = el.full_name;
      draft.swift = el.swift;
    });
  };

  const formatDate = useCallback(
    (date: string) =>
      new Date(date).toLocaleDateString(languageCode, DATE_OPTIONS).replace(/\./g, ''),
    [languageCode]
  );

  return (
    <SearchInput<DataElementType>
      className={cn(null, [className])}
      data-testid={ti()}
      label={t('public.iban')}
      placeholder={t('sepa.iban_placeholder')}
      data={{
        [t('iban.recent_transfers')]: data,
      }}
      customOption={option => <CustomOption data={option} date={formatDate(option.created_at)} />}
      onChange={handleChange}
      onInput={value => {
        setContext(draft => {
          draft.iban = value;
        });
      }}
      error={error}
      outerValue={iban}
    />
  );
};

export default RecentTransfers;
